import type { ISandboxState } from './types';
import { types } from './types';

export const initialState: ISandboxState = {
    explore: null,
    course: null,
    teacherDashboard: null,
    myEnrollments: null,
    myAssignments: null,
    teacherReviews: null,
    lessonProject: null,
    lessonAssignments: null,
    classAssets: null,
    paymentHistory: null,
    myOfficeHours: null,
    teacherOfficeHours: null,
    teacherAvailability: null,
    hostBookings: null,
    studentBookings: null,

    fetchingExplore: false,
    fetchingCourse: false,
    fetchingTeacherDashboard: false,
    fetchingEnrollments: false,
    fetchingAssignments: false,
    fetchingTeacherReviews: false,
    fetchingHostBookings: false,
    fetchingStudentBookings: false,
    fetchingMyOfficeHours: false,
    fetchingPaymentHistory: false,
    mutating: false,
    error: null,
};

function setFetching(state: ISandboxState, key: keyof ISandboxState, value: boolean): ISandboxState {
    return { ...state, [key]: value, error: value ? null : state.error };
}

export function sandboxReducer(state = initialState, action: any): ISandboxState {
    switch (action.type) {
        case types.FETCH_EXPLORE:
            return setFetching(state, 'fetchingExplore', true);
        case types.FETCH_EXPLORE_SUCCESS:
            return { ...state, fetchingExplore: false, explore: action.payload };
        case types.FETCH_EXPLORE_FAIL:
            return { ...state, fetchingExplore: false, error: action.payload };

        case types.FETCH_COURSE:
            return setFetching(state, 'fetchingCourse', true);
        case types.FETCH_COURSE_SUCCESS:
            return { ...state, fetchingCourse: false, course: action.payload };
        case types.FETCH_COURSE_FAIL:
            return { ...state, fetchingCourse: false, error: action.payload };

        case types.FETCH_TEACHER_DASHBOARD:
            return setFetching(state, 'fetchingTeacherDashboard', true);
        case types.FETCH_TEACHER_DASHBOARD_SUCCESS:
            return { ...state, fetchingTeacherDashboard: false, teacherDashboard: action.payload };
        case types.FETCH_TEACHER_DASHBOARD_FAIL:
            return { ...state, fetchingTeacherDashboard: false, error: action.payload };

        case types.FETCH_MY_ENROLLMENTS:
            return setFetching(state, 'fetchingEnrollments', true);
        case types.FETCH_MY_ENROLLMENTS_SUCCESS:
            return { ...state, fetchingEnrollments: false, myEnrollments: action.payload };
        case types.FETCH_MY_ENROLLMENTS_FAIL:
            return { ...state, fetchingEnrollments: false, error: action.payload };

        case types.FETCH_MY_ASSIGNMENTS:
            return setFetching(state, 'fetchingAssignments', true);
        case types.FETCH_MY_ASSIGNMENTS_SUCCESS:
            return { ...state, fetchingAssignments: false, myAssignments: action.payload };
        case types.FETCH_MY_ASSIGNMENTS_FAIL:
            return { ...state, fetchingAssignments: false, error: action.payload };

        case types.FETCH_TEACHER_REVIEWS:
            return setFetching(state, 'fetchingTeacherReviews', true);
        case types.FETCH_TEACHER_REVIEWS_SUCCESS:
            return { ...state, fetchingTeacherReviews: false, teacherReviews: action.payload };
        case types.FETCH_TEACHER_REVIEWS_FAIL:
            return { ...state, fetchingTeacherReviews: false, error: action.payload };

        case types.FETCH_LESSON_PROJECT_SUCCESS:
            return { ...state, lessonProject: action.payload };
        case types.FETCH_LESSON_ASSIGNMENTS_SUCCESS:
            return { ...state, lessonAssignments: action.payload };
        case types.FETCH_CLASS_ASSETS_SUCCESS:
            return { ...state, classAssets: action.payload };
        case types.FETCH_PAYMENT_HISTORY:
            return setFetching(state, 'fetchingPaymentHistory', true);
        case types.FETCH_PAYMENT_HISTORY_SUCCESS:
            return { ...state, fetchingPaymentHistory: false, paymentHistory: action.payload };
        case types.FETCH_PAYMENT_HISTORY_FAIL:
            return { ...state, fetchingPaymentHistory: false, error: action.payload };

        case types.FETCH_MY_OFFICE_HOURS:
            return setFetching(state, 'fetchingMyOfficeHours', true);
        case types.FETCH_MY_OFFICE_HOURS_SUCCESS:
            return { ...state, fetchingMyOfficeHours: false, myOfficeHours: action.payload };
        case types.FETCH_MY_OFFICE_HOURS_FAIL:
            return { ...state, fetchingMyOfficeHours: false, error: action.payload };
        case types.UPSERT_MY_OFFICE_HOURS_SUCCESS:
            return { ...state, myOfficeHours: action.payload, mutating: false };

        case types.FETCH_TEACHER_OFFICE_HOURS_SUCCESS:
            return { ...state, teacherOfficeHours: action.payload };
        case types.FETCH_TEACHER_OH_AVAILABILITY_SUCCESS:
            return { ...state, teacherAvailability: action.payload };

        case types.FETCH_HOST_BOOKINGS:
            return setFetching(state, 'fetchingHostBookings', true);
        case types.FETCH_HOST_BOOKINGS_SUCCESS:
            return { ...state, fetchingHostBookings: false, hostBookings: action.payload };
        case types.FETCH_HOST_BOOKINGS_FAIL:
            return { ...state, fetchingHostBookings: false, error: action.payload };

        case types.FETCH_STUDENT_BOOKINGS:
            return setFetching(state, 'fetchingStudentBookings', true);
        case types.FETCH_STUDENT_BOOKINGS_SUCCESS:
            return { ...state, fetchingStudentBookings: false, studentBookings: action.payload };
        case types.FETCH_STUDENT_BOOKINGS_FAIL:
            return { ...state, fetchingStudentBookings: false, error: action.payload };

        case types.CREATE_COURSE:
        case types.UPDATE_COURSE:
        case types.PUBLISH_COURSE:
        case types.ENROLL_FREE:
        case types.COMPLETE_PURCHASE:
        case types.SUBMIT_ASSIGNMENT:
        case types.REVIEW_ASSIGNMENT:
        case types.ADD_CLASS_ASSET:
        case types.UPDATE_CLASS_ASSET:
        case types.DELETE_CLASS_ASSET:
        case types.UPSERT_COURSE_RATING:
        case types.UPSERT_MY_OFFICE_HOURS:
        case types.BOOK_TEACHER_OFFICE_HOURS:
        case types.CANCEL_HOST_BOOKING:
        case types.CANCEL_STUDENT_BOOKING:
        case types.RESCHEDULE_STUDENT_BOOKING:
        case types.REQUEST_STUDENT_OH_REFUND:
            return { ...state, mutating: true, error: null };

        case types.CREATE_COURSE_SUCCESS:
        case types.UPDATE_COURSE_SUCCESS:
        case types.PUBLISH_COURSE_SUCCESS:
            return { ...state, mutating: false, course: action.payload };
        case types.CREATE_COURSE_FAIL:
        case types.UPDATE_COURSE_FAIL:
        case types.PUBLISH_COURSE_FAIL:
        case types.ENROLL_FREE_FAIL:
        case types.COMPLETE_PURCHASE_FAIL:
        case types.SUBMIT_ASSIGNMENT_FAIL:
        case types.REVIEW_ASSIGNMENT_FAIL:
        case types.ADD_CLASS_ASSET_FAIL:
        case types.UPDATE_CLASS_ASSET_FAIL:
        case types.DELETE_CLASS_ASSET_FAIL:
        case types.UPSERT_COURSE_RATING_FAIL:
        case types.UPSERT_MY_OFFICE_HOURS_FAIL:
        case types.BOOK_TEACHER_OFFICE_HOURS_FAIL:
        case types.CANCEL_HOST_BOOKING_FAIL:
        case types.CANCEL_STUDENT_BOOKING_FAIL:
        case types.RESCHEDULE_STUDENT_BOOKING_FAIL:
        case types.REQUEST_STUDENT_OH_REFUND_FAIL:
            return { ...state, mutating: false, error: action.payload };

        case types.ENROLL_FREE_SUCCESS:
        case types.COMPLETE_PURCHASE_SUCCESS:
        case types.SUBMIT_ASSIGNMENT_SUCCESS:
        case types.REVIEW_ASSIGNMENT_SUCCESS:
        case types.ADD_CLASS_ASSET_SUCCESS:
        case types.UPDATE_CLASS_ASSET_SUCCESS:
        case types.DELETE_CLASS_ASSET_SUCCESS:
        case types.UPSERT_COURSE_RATING_SUCCESS:
        case types.BOOK_TEACHER_OFFICE_HOURS_SUCCESS:
        case types.CANCEL_HOST_BOOKING_SUCCESS:
        case types.CANCEL_STUDENT_BOOKING_SUCCESS:
        case types.RESCHEDULE_STUDENT_BOOKING_SUCCESS:
        case types.REQUEST_STUDENT_OH_REFUND_SUCCESS:
            return { ...state, mutating: false };

        default:
            return state;
    }
}
